import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { ChevronRight, ChevronLeft, Check, TrendingUp, ArrowUp, ArrowRight, ArrowDown, Lock, Sparkles } from 'lucide-react';

// Wealth Stage definitions with levels
const WEALTH_STAGES = [
  {
    key: 'supported',
    name: 'Supported',
    level: 1,
    description: 'Your financial foundation depends on external support. This is the starting point — building independent income is the first priority.',
    color: '#6B7280'
  },
  {
    key: 'builder',
    name: 'Builder',
    level: 2,
    description: 'You generate income but lack financial cushion. Builders are actively working toward stability, but resilience remains fragile.',
    color: '#8B5CF6'
  },
  {
    key: 'stabilizer',
    name: 'Stabilizer',
    level: 3,
    description: 'You have built a buffer against uncertainty. Stabilizers can weather short-term disruptions and are positioned to begin accumulating.',
    color: '#3B82F6'
  },
  {
    key: 'accumulator',
    name: 'Accumulator',
    level: 4,
    description: 'You are actively building assets and creating partial financial autonomy. The transition from earned income to investment income is underway.',
    color: '#10B981'
  },
  {
    key: 'investor',
    name: 'Investor',
    level: 5,
    description: 'Your investments generate meaningful returns. Passive income streams are reducing your dependency on active work.',
    color: '#F59E0B'
  },
  {
    key: 'lifeOwner',
    name: 'Life Owner',
    level: 6,
    description: 'Your passive income fully covers your lifestyle. Time is your own. Financial decisions are now about optimization, not survival.',
    color: '#EC4899'
  },
  {
    key: 'legacyDesigner',
    name: 'Legacy Designer',
    level: 7,
    description: 'Your wealth exceeds your needs. You are designing impact, legacy, and meaning beyond personal finance.',
    color: '#C18010'
  }
];

// Direction definitions
const DIRECTIONS = {
  advancing: {
    key: 'advancing',
    label: 'Advancing',
    icon: ArrowUp,
    color: '#10B981',
    description: 'Your income and resilience are strengthening. You are moving toward the next stage.'
  },
  steady: {
    key: 'steady',
    label: 'Steady',
    icon: ArrowRight,
    color: '#F59E0B',
    description: 'Your position is stable. Focus on building momentum to advance.'
  },
  contracting: {
    key: 'contracting',
    label: 'Contracting',
    icon: ArrowDown,
    color: '#EF4444',
    description: 'Your financial position is under pressure. Stabilizing should be the immediate priority.'
  }
};

// Updated Questions
const QUESTIONS = [
  {
    id: 1,
    question: 'If you stopped working today, how would you pay for your current lifestyle?',
    options: [
      { id: 'depend', label: "I wouldn't be able to, I would depend on others or on debt", value: 1 },
      { id: 'savings', label: 'My savings would cover me temporarily', value: 2 },
      { id: 'partial', label: 'My investments or business income would allow me to survive, but I would have to cut some expenses', value: 3 },
      { id: 'full', label: 'My investments could fully cover my lifestyle', value: 4 },
      { id: 'surplus', label: "My investment income far exceeds my lifestyle spending, it wouldn't be a problem", value: 5 }
    ]
  },
  {
    id: 2,
    question: 'Right now, does the money you earn from work cover your monthly expenses?',
    options: [
      { id: 'no', label: 'No', value: 1 },
      { id: 'barely', label: 'Barely', value: 2 },
      { id: 'yes', label: 'Yes', value: 3 },
      { id: 'surplus', label: 'Yes, with money left over', value: 4 }
    ]
  },
  {
    id: 3,
    question: 'If all income stopped, how long could you maintain your current lifestyle?',
    options: [
      { id: 'less_1', label: 'Less than 1 month', value: 1 },
      { id: '1_3', label: '1–3 months', value: 2 },
      { id: '3_12', label: '3–12 months', value: 3 },
      { id: 'more_1yr', label: 'More than 1 year', value: 4 },
      { id: 'indefinite', label: 'Indefinitely', value: 5 }
    ]
  }
];

// Analysis phrases
const ANALYSIS_PHRASES = [
  'Evaluating autonomy...',
  'Measuring resilience...',
  'Calculating trajectory...',
  'Positioning on ProsperMind Ladder...'
];

// Decision Engine - Calculate Stage based on Q1, Q2, Q3
const calculateStage = (q1, q2, q3) => {
  // Q1 = Option 1 (depend) → Supported
  if (q1 === 1) {
    return 'supported';
  }
  
  // Q1 = Option 2 (savings)
  if (q1 === 2) {
    // IF Q2 = No OR Barely AND Q3 < 3 months → Builder
    if ((q2 === 1 || q2 === 2) && q3 <= 2) {
      return 'builder';
    }
    // ELSE → Stabilizer
    return 'stabilizer';
  }
  
  // Q1 = Option 3 (partial)
  if (q1 === 3) {
    // IF Q2 = Yes or Yes with surplus AND Q3 >= 3 months → Investor
    if ((q2 === 3 || q2 === 4) && q3 >= 3) {
      return 'investor';
    }
    // ELSE → Accumulator
    return 'accumulator';
  }
  
  // Q1 = Option 4 (full) → Life Owner
  if (q1 === 4) {
    return 'lifeOwner';
  }
  
  // Q1 = Option 5 (surplus) → Legacy Designer
  if (q1 === 5) {
    return 'legacyDesigner';
  }
  
  return 'builder'; // Default fallback
};

// Decision Engine - Calculate Direction based on Q2, Q3
const calculateDirection = (q2, q3) => {
  // IF Q2 = Yes or Yes with surplus AND Q3 >= 3 months → Advancing
  if ((q2 === 3 || q2 === 4) && q3 >= 3) {
    return 'advancing';
  }
  
  // IF Q2 = Barely OR Q3 = 1–3 months → Steady
  if (q2 === 2 || q3 === 2) {
    return 'steady';
  }
  
  // IF Q2 = No OR Q3 = Less than 1 month → Contracting
  if (q2 === 1 || q3 === 1) {
    return 'contracting';
  }
  
  return 'steady'; // Default
};

// Generate dynamic explanation
const generateExplanation = (stage, direction) => {
  const stageInfo = WEALTH_STAGES.find(s => s.key === stage);
  const directionInfo = DIRECTIONS[direction];
  const nextStage = WEALTH_STAGES.find(s => s.level === stageInfo.level + 1);
  
  let explanation = `You are currently a ${stageInfo.name}. ${stageInfo.description}\n\n`;
  explanation += `Your trajectory is ${directionInfo.label}, ${directionInfo.description.toLowerCase()}`;
  
  if (nextStage && direction === 'advancing') {
    explanation += `\n\nTo move toward ${nextStage.name} stage, focus on increasing income that does not require your time.`;
  } else if (nextStage && direction === 'steady') {
    explanation += `\n\nTo accelerate toward ${nextStage.name}, prioritize building passive income streams and extending your financial runway.`;
  } else if (direction === 'contracting') {
    explanation += `\n\nPrioritize stabilizing your income and building an emergency buffer before focusing on growth.`;
  }
  
  return explanation;
};

const QuickQuizModule = ({ onComplete, onNavigate }) => {
  const [screen, setScreen] = useState('intro'); // intro, question, analyzing, result
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [analysisPhrase, setAnalysisPhrase] = useState(0);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [showCheck, setShowCheck] = useState(false);

  // Analysis animation
  useEffect(() => {
    if (screen === 'analyzing') {
      // Cycle through phrases
      const phraseInterval = setInterval(() => {
        setAnalysisPhrase(prev => (prev + 1) % ANALYSIS_PHRASES.length);
      }, 1200);

      // Progress bar animation (non-linear)
      const progressInterval = setInterval(() => {
        setAnalysisProgress(prev => {
          if (prev >= 100) return 100;
          const increment = Math.max(1, 15 - (prev / 10));
          return Math.min(100, prev + increment);
        });
      }, 100);

      // Transition to result after 5 seconds
      const timeout = setTimeout(() => {
        const [q1, q2, q3] = answers;
        const stageKey = calculateStage(q1, q2, q3);
        const directionKey = calculateDirection(q2, q3);
        const stage = WEALTH_STAGES.find(s => s.key === stageKey);
        const direction = DIRECTIONS[directionKey];
        
        const calculatedResult = {
          stage,
          direction,
          answers,
          explanation: generateExplanation(stageKey, directionKey)
        };
        
        setResult(calculatedResult);
        setScreen('result');
        
        // Automatically save the result when calculated (so it persists even if modal is closed)
        if (onComplete) {
          onComplete({
            stage_key: stage.key,
            stage_name: stage.name,
            stage_level: stage.level,
            direction: direction.key,
            answers: answers
          });
        }
      }, 5000);

      return () => {
        clearInterval(phraseInterval);
        clearInterval(progressInterval);
        clearTimeout(timeout);
      };
    }
  }, [screen, answers, onComplete]);

  const handleStart = () => {
    setScreen('question');
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option.id);
    setShowCheck(true);

    setTimeout(() => {
      const newAnswers = [...answers, option.value];
      setAnswers(newAnswers);
      setShowCheck(false);
      setSelectedOption(null);

      if (currentQuestion < QUESTIONS.length - 1) {
        setCurrentQuestion(prev => prev + 1);
      } else {
        setTimeout(() => {
          setScreen('analyzing');
        }, 500);
      }
    }, 600);
  };

  const handleSeeRoadmap = () => {
    // Result is already saved when analysis completes, just navigate
    if (onNavigate) {
      onNavigate('goals');
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      // Go back to previous question
      setCurrentQuestion(prev => prev - 1);
      // Remove the last answer so user can re-answer
      setAnswers(prev => prev.slice(0, -1));
      setSelectedOption(null);
      setShowCheck(false);
    } else {
      // If on first question, go back to intro
      setScreen('intro');
    }
  };

  const handleContinueToDashboard = () => {
    // Result is already saved when analysis completes, just navigate
    if (onNavigate) {
      onNavigate('dashboard');
    }
  };

  // Intro Screen
  if (screen === 'intro') {
    return (
      <div className="min-h-[600px] flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="mb-8">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-light text-gray-900 mb-4 tracking-tight">
              Let's take your financial pulse.
            </h1>
            <p className="text-gray-500 text-lg">
              3 quick questions. 45 seconds. Immediate clarity.
            </p>
          </div>
          
          <Button
            onClick={handleStart}
            data-testid="quick-quiz-start-btn"
            className="px-12 py-4 text-lg font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-full transition-all duration-300 transform hover:scale-105"
          >
            Start
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Question Screen
  if (screen === 'question') {
    const question = QUESTIONS[currentQuestion];
    
    return (
      <div className="min-h-[600px] flex flex-col bg-gradient-to-b from-gray-50 to-white">
        {/* Header with Back button and Progress */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handleBack}
              data-testid="quiz-back-btn"
              className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors duration-200 text-sm font-medium"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>
            <span className="text-sm text-gray-400 font-medium tracking-wide">Question {currentQuestion + 1} of {QUESTIONS.length}</span>
          </div>
          <Progress value={((currentQuestion + 1) / QUESTIONS.length) * 100} className="h-1 bg-gray-200" />
        </div>

        {/* Question */}
        <div className="flex-1 flex flex-col justify-center px-6 pb-12">
          <h2 className="text-xl md:text-2xl font-medium text-gray-900 mb-8 text-center leading-relaxed">
            {question.question}
          </h2>

          {/* Options */}
          <div className="space-y-3 max-w-lg mx-auto w-full">
            {question.options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleOptionSelect(option)}
                disabled={selectedOption !== null}
                data-testid={`quiz-option-${option.id}`}
                className={`
                  w-full p-4 text-left rounded-xl border-2 transition-all duration-300
                  ${selectedOption === option.id 
                    ? 'border-gray-900 bg-gray-900 text-white transform scale-[1.02]' 
                    : 'border-gray-200 bg-white hover:border-gray-400 hover:shadow-md text-gray-700'
                  }
                  ${selectedOption !== null && selectedOption !== option.id ? 'opacity-50' : ''}
                `}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm md:text-base">{option.label}</span>
                  {selectedOption === option.id && showCheck && (
                    <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center animate-pulse">
                      <Check className="w-4 h-4 text-gray-900" />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Analysis Screen
  if (screen === 'analyzing') {
    return (
      <div className="min-h-[600px] flex items-center justify-center bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gray-800/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gray-700/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          {/* Subtle grid lines */}
          <div className="absolute inset-0 opacity-5">
            <svg className="w-full h-full">
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
          {/* Motion particles */}
          <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-white/20 rounded-full animate-ping"></div>
          <div className="absolute top-1/3 left-2/3 w-1 h-1 bg-white/30 rounded-full animate-ping delay-500"></div>
          <div className="absolute top-2/3 left-1/3 w-1.5 h-1.5 bg-white/25 rounded-full animate-ping delay-700"></div>
        </div>

        <div className="relative z-10 text-center px-6">
          <h2 className="text-2xl font-light text-white mb-8 tracking-wide">
            Mapping your financial structure...
          </h2>
          
          <p className="text-gray-400 text-lg mb-12 h-8 transition-opacity duration-500">
            {ANALYSIS_PHRASES[analysisPhrase]}
          </p>

          {/* Progress bar */}
          <div className="w-64 mx-auto">
            <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-gray-400 to-white rounded-full transition-all duration-100 ease-out"
                style={{ width: `${analysisProgress}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Result Screen
  if (screen === 'result' && result) {
    const DirectionIcon = result.direction.icon;
    const currentLevel = result.stage.level;

    return (
      <div className="min-h-[600px] bg-gradient-to-b from-white to-gray-50 py-8 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6">
          {/* Stage Header */}
          <div className="text-center mb-8">
            <p className="text-gray-500 text-sm uppercase tracking-widest mb-4">
              You are currently
            </p>

            {/* Stage Badge */}
            <div className="relative inline-block mb-4">
              <div 
                className="px-10 py-5 rounded-xl text-white font-bold text-3xl shadow-2xl"
                style={{ 
                  background: `linear-gradient(135deg, ${result.stage.color} 0%, ${result.stage.color}dd 100%)`,
                  boxShadow: `0 20px 40px ${result.stage.color}40`
                }}
              >
                {result.stage.name}
              </div>
            </div>

            {/* Direction Badge */}
            <div className="flex items-center justify-center gap-2 mt-4">
              <div 
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                style={{ 
                  backgroundColor: `${result.direction.color}15`,
                  color: result.direction.color
                }}
              >
                <DirectionIcon 
                  className="w-4 h-4 animate-bounce" 
                  style={{ 
                    animationDuration: result.direction.key === 'steady' ? '0s' : '1s',
                    transform: result.direction.key === 'contracting' ? 'rotate(180deg)' : 'none'
                  }}
                />
                {result.direction.label}
              </div>
            </div>
          </div>

          {/* Ladder Visualization */}
          <div className="bg-gray-900 rounded-2xl p-6 mb-8">
            <h3 className="text-white text-sm font-medium mb-4 text-center tracking-wide">PROSPERMIND LADDER</h3>
            <div className="relative">
              {WEALTH_STAGES.slice().reverse().map((stage, index) => {
                const isCurrentStage = stage.key === result.stage.key;
                const isPastStage = stage.level < currentLevel;
                const isFutureStage = stage.level > currentLevel;
                const isAdjacentStage = Math.abs(stage.level - currentLevel) === 1;
                
                return (
                  <div 
                    key={stage.key}
                    className={`
                      relative flex items-center py-3 px-4 rounded-lg mb-2 transition-all duration-300
                      ${isCurrentStage ? 'bg-gray-800 border-l-4' : 'bg-gray-800/30'}
                      ${isFutureStage && !isAdjacentStage ? 'opacity-30 blur-[1px]' : ''}
                      ${isPastStage && !isAdjacentStage ? 'opacity-30 blur-[1px]' : ''}
                    `}
                    style={{ 
                      borderColor: isCurrentStage ? stage.color : 'transparent'
                    }}
                  >
                    {/* Level number */}
                    <div 
                      className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-4
                        ${isCurrentStage ? 'text-white' : 'text-gray-500'}
                      `}
                      style={{ 
                        backgroundColor: isCurrentStage ? stage.color : 'transparent',
                        border: isCurrentStage ? 'none' : '1px solid #374151'
                      }}
                    >
                      {stage.level}
                    </div>
                    
                    {/* Stage name */}
                    <span 
                      className={`
                        font-medium flex-1
                        ${isCurrentStage ? 'text-white' : 'text-gray-400'}
                      `}
                    >
                      {stage.name}
                    </span>

                    {/* Direction arrow for current stage */}
                    {isCurrentStage && (
                      <DirectionIcon 
                        className="w-5 h-5 animate-pulse"
                        style={{ color: result.direction.color }}
                      />
                    )}

                    {/* Lock icon for non-adjacent future stages */}
                    {isFutureStage && !isAdjacentStage && (
                      <Lock className="w-4 h-4 text-gray-600" />
                    )}
                  </div>
                );
              })}
              
              {/* Premium tease */}
              <p className="text-gray-600 text-xs text-center mt-4">
                Unlock full ladder access with Core.
              </p>
            </div>
          </div>

          {/* Explanation Block */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {result.explanation}
            </p>
          </div>

          {/* Premium Tease - Blurred Content */}
          <div className="relative mb-8">
            <div className="bg-gray-100 rounded-xl p-6 blur-sm select-none">
              <h4 className="text-gray-800 font-semibold mb-3">Your Milestone Checklist</h4>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• Build emergency fund to $10,000</li>
                <li>• Increase passive income to 25% of expenses</li>
                <li>• Reduce debt-to-income ratio below 30%</li>
              </ul>
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl">
              <div className="text-center">
                <Lock className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600 text-sm font-medium">Unlock with Core</p>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="space-y-3">
            <Button
              onClick={handleSeeRoadmap}
              data-testid="quiz-see-roadmap-btn"
              className="w-full py-4 text-lg font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-full transition-all duration-300"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              See My Roadmap
            </Button>
            
            <Button
              onClick={handleContinueToDashboard}
              variant="ghost"
              data-testid="quiz-continue-dashboard-btn"
              className="w-full py-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all duration-300"
            >
              Continue to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Start Here Button Component for Dashboard
export const QuickQuizStartButton = ({ hasCompletedQuiz, onClick }) => {
  if (hasCompletedQuiz) {
    return null;
  }

  return (
    <div className="flex items-center justify-center py-12">
      <Button
        onClick={onClick}
        data-testid="start-here-btn"
        className="px-12 py-6 text-xl font-semibold bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white rounded-full shadow-2xl transition-all duration-300 transform hover:scale-105"
      >
        <TrendingUp className="w-6 h-6 mr-3" />
        Start Here
      </Button>
    </div>
  );
};

export default QuickQuizModule;
